// Mirrors the backend's LIFT_TO_1RM_FIELD map so the public-plan
// "Tune to me" toggle can rescale weights client-side without an extra API call.
export const LIFT_TO_1RM = {
  "back-squat": "squat_1rm_lb",
  "front-squat": "squat_1rm_lb",
  "zercher-squat": "squat_1rm_lb",
  "db-front-squat": "squat_1rm_lb",
  "sandbag-zercher-squat": "squat_1rm_lb",
  "bench-press": "bench_1rm_lb",
  "incline-bench-press": "bench_1rm_lb",
  "close-grip-bench": "bench_1rm_lb",
  "db-floor-press": "bench_1rm_lb",
  "barbell-deadlift": "deadlift_1rm_lb",
  "sumo-deadlift": "deadlift_1rm_lb",
  "stiff-leg-deadlift": "deadlift_1rm_lb",
  "rack-pull": "deadlift_1rm_lb",
  "acft-deadlift": "deadlift_1rm_lb",
  "deadlift-cluster": "deadlift_1rm_lb",
  "barbell-rdl": "deadlift_1rm_lb",
  "trap-bar-rdl": "deadlift_1rm_lb",
  "overhead-press": "ohp_1rm_lb",
  "push-press": "ohp_1rm_lb",
  "landmine-press": "ohp_1rm_lb",
  "band-overhead-press": "ohp_1rm_lb",
};

// Plyometric / power exercise IDs — mirrors the backend's PLYO_IDS for the
// auto-taper feature. Keep both lists in sync if you add new plyo drills.
export const PLYO_IDS = new Set([
  "box-jump", "broad-jump", "jump-squat", "depth-jump",
  "split-squat-jump", "trap-bar-jump", "box-jump-intervals",
  "tuck-jump", "knee-tuck-jump", "single-leg-broad-jump",
  "hurdle-hop", "drop-jump", "jump-lunge", "single-leg-box-jump",
  "burpee-broad-jump",
  "clap-pushup", "plyo-pushup", "medball-slam", "standing-power-throw",
  "mb-rotational-throw", "mb-overhead-throw", "mb-chest-pass",
  "pogo-hop", "ankle-hop", "lateral-bound", "skater-jump", "power-skip",
]);

// Anaerobic / interval-style cardio IDs (HIIT, sprints, intervals).
export const ANAEROBIC_IDS = new Set([
  "sprint-intervals", "200m-sprints", "shuttle-sprint-100m", "shuttle-run",
  "hill-sprints", "stair-sprints", "burpees", "burpee-pyramid",
  "burpee-broad-jump", "tabata-bodyweight", "bike-tabata",
  "assault-bike-intervals", "rower-500m-repeats", "rowing-intervals",
  "rowing-2k", "ski-erg-intervals", "swim-intervals",
  "battle-rope-waves", "jacobs-ladder", "ruck-shuttle",
]);

// Aerobic / steady-state cardio IDs (Zone 2 / long efforts).
export const AEROBIC_IDS = new Set([
  "tempo-run", "easy-run", "long-slow-distance", "ruck-march",
  "rowing-easy-distance", "bike-tempo", "swim-freestyle",
  "swim-pull-set", "aquajog", "recovery-spin",
]);

const WEIGHT_PCT_RE = /(\d+(?:\.\d+)?)\s*lb.*?(\d+(?:\.\d+)?)\s*%/i;

/**
 * Apply a user's assessment to plan weeks, rescaling main-lift weights based on
 * the embedded "(X% 1RM)" annotation. Returns { weeks, count } where count is
 * the number of prescriptions actually rewritten.
 */
// Starting intensity (% 1RM) for a main barbell lift by skill level.
// Mirrors the conservative ramp-up used by the AI plan generator.
const LEVEL_PCT = {
  Beginner: 65,
  Intermediate: 70,
  Advanced: 75,
};

/**
 * Compute a "{lb} lb ({pct}% 1RM)" string for a main barbell lift if the user
 * has the relevant 1RM logged. Returns null if the exercise isn't a tracked
 * main lift, or the user has no 1RM logged for it.
 */
export function computeMainLiftWeight(exId, assessment, level = "Intermediate") {
  const rmField = LIFT_TO_1RM[exId];
  if (!rmField) return null;
  if (!assessment || !assessment[rmField]) return null;
  const pct = LEVEL_PCT[level] ?? 70;
  const rounded = Math.round((parseFloat(assessment[rmField]) * pct) / 100 / 5) * 5;
  return `${rounded} lb (${pct}% 1RM)`;
}

/**
 * Walk an array of exercise entries (from a focus blueprint or anywhere) and
 * fill in ANY missing/empty main-lift weight from the user's saved assessment.
 * Preserves explicit blueprint values like "BW" or "10 lb ball" — only acts
 * when weight is "" / undefined / null.
 */
export function applyAssessmentToBlueprint(items, assessment, level = "Intermediate") {
  if (!items || !items.length) return items;
  return items.map((it) => {
    const hasWeight = typeof it.weight === "string" && it.weight.trim() !== "";
    if (hasWeight) return it;
    const auto = computeMainLiftWeight(it.exercise_id, assessment, level);
    return auto ? { ...it, weight: auto } : it;
  });
}

/**
 * Build a default exercise prescription when a user manually adds a drill in
 * the Plan Builder. If the drill is a main barbell lift AND the user has the
 * relevant 1RM logged on their assessment, the weight is prefilled with the
 * exact "{lb} lb ({pct}% 1RM)" format the rest of the app expects.
 */
export function defaultPrescription(exDef, assessment, level = "Intermediate") {
  const base = { exercise_id: exDef.id, sets: 3, reps: "8-10", notes: "" };
  if (!exDef) return base;

  const domain = exDef.domain;
  const equipment = exDef.equipment;

  // Domain-specific defaults
  if (domain === "Strength") {
    base.sets = level === "Beginner" ? 3 : 4;
    base.reps = level === "Beginner" ? "8-10" : "5";
  } else if (domain === "Endurance") {
    base.sets = 1;
    base.reps = "20 min";
  } else if (domain === "Mobility") {
    base.sets = 2;
    base.reps = "30-60s";
  } else if (domain === "ACFT") {
    base.sets = 3;
    base.reps = "5";
  }

  // Auto-fill weight for main barbell lifts using the user's logged 1RM.
  const auto = computeMainLiftWeight(exDef.id, assessment, level);
  if (auto) {
    base.weight = auto;
  } else if (domain === "Strength" && equipment !== "None" && equipment !== "Resistance Band") {
    // Accessory lift — leave weight blank but signal intent.
    base.weight = "";
  }

  return base;
}

export function rescaleWeeksForUser(weeks, assessment) {
  if (!weeks || !assessment) return { weeks, count: 0 };
  let count = 0;
  const out = weeks.map((w) => ({
    ...w,
    days: (w.days || []).map((d) => ({
      ...d,
      exercises: (d.exercises || []).map((ex) => {
        const rmField = LIFT_TO_1RM[ex.exercise_id];
        if (!rmField) return ex;
        const userRM = assessment[rmField];
        if (!userRM) return ex;
        const m = WEIGHT_PCT_RE.exec(ex.weight || "");
        if (!m) return ex;
        const pct = parseFloat(m[2]);
        const rounded = Math.round((parseFloat(userRM) * pct) / 100 / 5) * 5;
        count += 1;
        return { ...ex, weight: `${rounded} lb (${Math.round(pct)}% 1RM)` };
      }),
    })),
  }));
  return { weeks: out, count };
}
